import { cleanCpf } from '../services/utils/stringUtils';
import { asPersistedContractEntityId } from '../services/contracts/buildContractUpdatePayload';

export type LineModalRepresentativeLike = {
  id?: string;
  nome?: string;
  cpf?: string;
  rg?: string;
  email?: string;
  telefone?: string;
};

function normalizeMergedRep(
  rep: LineModalRepresentativeLike,
  prior?: LineModalRepresentativeLike
): LineModalRepresentativeLike {
  return {
    ...(prior ?? {}),
    ...rep,
    id: rep.id?.trim() || prior?.id,
    nome: rep.nome ?? prior?.nome ?? '',
    cpf: rep.cpf ?? prior?.cpf ?? '',
    rg: rep.rg ?? prior?.rg,
    email: (rep.email ?? prior?.email ?? '').trim(),
    telefone: rep.telefone ?? prior?.telefone ?? '',
  };
}

function existingMatchKeys(rep: LineModalRepresentativeLike, index: number): string[] {
  const keys: string[] = [];
  const id = rep.id?.trim();
  if (id) {
    keys.push(`id:${id.toLowerCase()}`);
  }
  const cpf = cleanCpf(rep.cpf ?? '');
  if (cpf.length === 11) {
    keys.push(`cpf:${cpf}`);
  }
  keys.push(`idx:${index}`);
  return keys;
}

function markExistingMatched(
  rep: LineModalRepresentativeLike | undefined,
  index: number,
  matchedKeys: Set<string>
): void {
  if (!rep) return;
  for (const key of existingMatchKeys(rep, index)) {
    matchedKeys.add(key);
  }
}

function isExistingRepMatched(rep: LineModalRepresentativeLike, index: number, matchedKeys: Set<string>): boolean {
  return existingMatchKeys(rep, index).some((key) => matchedKeys.has(key));
}

/**
 * O endpoint line-details-modal pode devolver só nome/cpf (summary) e, às vezes,
 * menos entradas que a lista/contrato já conhecido. Mescla summary com dados
 * completos e mantém sócios persistidos ausentes do payload do modal.
 */
export function mergeLineModalRepresentatives(
  fromModal: LineModalRepresentativeLike[] | undefined | null,
  existing: LineModalRepresentativeLike[] | undefined | null
): LineModalRepresentativeLike[] {
  if (!fromModal?.length) {
    return existing?.length ? [...existing] : [];
  }

  const existingList = existing ?? [];
  const existingById = new Map<string, LineModalRepresentativeLike>();
  const existingByCpf = new Map<string, LineModalRepresentativeLike>();
  const matchedExistingKeys = new Set<string>();

  for (const rep of existingList) {
    if (rep.id?.trim()) {
      existingById.set(rep.id.trim().toLowerCase(), rep);
    }
    const cpf = cleanCpf(rep.cpf ?? '');
    if (cpf.length === 11) {
      existingByCpf.set(cpf, rep);
    }
  }

  const mergedFromModal = fromModal.map((modalRep, index) => {
    const modalId = modalRep.id?.trim();
    const modalCpf = cleanCpf(modalRep.cpf ?? '');
    const prior =
      (modalId ? existingById.get(modalId.toLowerCase()) : undefined) ??
      (modalCpf.length === 11 ? existingByCpf.get(modalCpf) : undefined) ??
      existingList[index];

    markExistingMatched(prior, index, matchedExistingKeys);
    return normalizeMergedRep(modalRep, prior);
  });

  const extrasFromExisting: LineModalRepresentativeLike[] = [];
  existingList.forEach((rep, index) => {
    if (isExistingRepMatched(rep, index, matchedExistingKeys)) {
      return;
    }
    // Só reintroduz sócios já persistidos — evita ressuscitar rascunhos sem id na API.
    if (!asPersistedContractEntityId(rep.id)) {
      return;
    }
    extrasFromExisting.push(normalizeMergedRep(rep));
  });

  return [...mergedFromModal, ...extrasFromExisting];
}

/** Chave estável para edição inline quando o summary não traz id. */
export function getLineModalRepresentativeEditKey(
  rep: LineModalRepresentativeLike,
  index: number
): string {
  const id = rep.id?.trim();
  if (id) return id;
  const cpf = cleanCpf(rep.cpf ?? '');
  if (cpf.length === 11) return `cpf:${cpf}`;
  return `idx:${index}`;
}

export function matchLineModalRepresentativeEditKey(
  rep: LineModalRepresentativeLike,
  index: number,
  editKey: string
): boolean {
  return getLineModalRepresentativeEditKey(rep, index) === editKey;
}
